import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Lock, Mail, Share2, Copy, Check, AlertCircle } from "lucide-react";
import PageShell from "../components/PageShell";
import RadialGauge from "../components/RadialGauge";
import BandPill from "../components/BandPill";
import DimensionBars from "../components/DimensionBars";
import SourcesList from "../components/SourcesList";
import EarningsList from "../components/EarningsList";
import CTABlock from "../components/CTABlock";
import HeroBandCTA from "../components/HeroBandCTA";
import CollapsibleSection from "../components/CollapsibleSection";
import CyclingLoader from "../components/CyclingLoader";
import { fullScore } from "../lib/api";
import { splitRevenueRange } from "../lib/format";

function bandForScore(score) {
  if (score >= 70) return "green";
  if (score >= 50) return "yellow";
  return "red";
}

export default function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = location.state;
  const [stage, setStage] = useState("provisional"); // provisional | researching | report
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [report, setReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!initial?.run_id) {
      navigate("/", { replace: true });
    }
  }, [initial, navigate]);

  const provisionalBand = useMemo(
    () => bandForScore(initial?.provisional_score ?? 0),
    [initial],
  );

  const onUnlock = async (e) => {
    e.preventDefault();
    setEmailError("");
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError("Enter a valid email so we can send your report.");
      return;
    }
    setSubmitting(true);
    setStage("researching");
    try {
      const r = await fullScore(initial.run_id, v);
      setReport(r);
      setStage("report");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Research could not complete. Please try again.";
      setEmailError(typeof msg === "string" ? msg : "Research could not complete.");
      setStage("provisional");
      setSubmitting(false);
    }
  };

  if (!initial?.run_id) return null;

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-16">
        <div className="mb-6">
          <Link to="/" className="text-sm text-[#b6c1b8] hover:text-[#4ADE80]" data-testid="back-home-link">
            ← Score a different idea
          </Link>
        </div>

        <div className="gl-card-lift p-5 sm:p-6 gl-card-active gl-fade-up" data-testid="provisional-card">
          <div className="gl-label mb-2">Your Idea</div>
          <p className="text-white text-base sm:text-lg leading-snug">
            {initial.idea_text}
          </p>
          {initial.category ? (
            <div className="mt-3">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[#4ADE80] border border-[#4ADE80]/30 bg-[#4ADE80]/10 px-2 py-1 rounded-full">
                {initial.category}
              </span>
            </div>
          ) : null}
        </div>

        {stage === "provisional" && (
          <div className="mt-8 sm:mt-12 gl-fade-up">
            <div className="flex flex-col items-center text-center">
              <div className="gl-label mb-3">Provisional Score</div>
              <div className="w-full max-w-[300px] sm:max-w-[340px]">
                <RadialGauge
                  score={initial.provisional_score}
                  band={provisionalBand}
                  size={320}
                  testId="provisional-score"
                />
              </div>
              <p className="mt-5 text-[#b6c1b8] max-w-lg text-sm sm:text-base leading-relaxed px-2">
                {initial.verdict_line}
              </p>
            </div>

            {/* Email gate, normal flow below the gauge */}
            <div className="mt-8 sm:mt-10 flex justify-center px-2">
              <form
                onSubmit={onUnlock}
                noValidate
                className="w-full max-w-md gl-card gl-card-active p-6 sm:p-8 text-center"
                data-testid="email-gate-form"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#4ADE80]/10 border border-[#4ADE80]/30 mb-4">
                  <Lock size={18} className="text-[#4ADE80]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Your idea scored {initial.provisional_score}.
                </h2>
                <p className="text-sm text-[#b6c1b8] mt-2 leading-relaxed">
                  Unlock the full breakdown, the proof, and exactly how to push it higher.
                </p>
                <div className="mt-5 space-y-2 text-left">
                  <label htmlFor="email" className="gl-label">
                    Email
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6c7770]"
                    />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      data-testid="email-gate-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@founder.com"
                      className="gl-input pl-11"
                      disabled={submitting}
                    />
                  </div>
                  {emailError ? (
                    <div
                      className="text-xs text-red-400 flex items-center gap-1"
                      data-testid="email-error"
                    >
                      <AlertCircle size={12} />
                      {emailError}
                    </div>
                  ) : null}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="unlock-report-button"
                  className="gl-cta gl-cta-block mt-5"
                >
                  {submitting ? "Unlocking..." : "Unlock the full report"}
                  {!submitting && <ArrowRight size={18} />}
                </button>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6c7770] mt-4 font-semibold">
                  Free. Takes 60 to 90 seconds.
                </p>
              </form>
            </div>
          </div>
        )}

        {stage === "researching" && (
          <div className="mt-12">
            <CyclingLoader />
          </div>
        )}

        {stage === "report" && report && (
          <ReportView report={report} initial={initial} copied={copied} setCopied={setCopied} />
        )}
      </section>
    </PageShell>
  );
}

function ReportView({ report, initial, copied, setCopied }) {
  const band = report.band || "yellow";
  const finalScore = report.final_score ?? 0;
  const shareUrl = report.share_id ? `${window.location.origin}/r/${report.share_id}` : "";
  const earningsCount = (report.earnings_evidence || []).filter(
    (e) => e && (e.reported_figure || "").trim() && (e.source_url || "").trim(),
  ).length;
  const sources = report.numbered_sources && report.numbered_sources.length
    ? report.numbered_sources
    : report.all_sources || [];
  const sourcesCount = (sources || []).filter((s) => s && s.url).length;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in iframes / non-HTTPS. Silent no-op is fine
      // here: the URL is also displayed as plain text next to the button so
      // the user can select-copy as a fallback.
    }
  };

  return (
    <div className="mt-6 space-y-6 gl-fade-up" data-testid="final-report">
      {/* HERO: Gauge + band pill, full-width verdict beneath */}
      <div className="gl-card-lift gl-card-active p-5 sm:p-7">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 items-center">
          {/* Gauge column - centered within its column */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-[240px]">
              <RadialGauge score={finalScore} band={band} size={220} testId="overall-score" />
            </div>
          </div>

          {/* Status column - vertically centered, just band pill (revenue lives in stat card below) */}
          <div className="flex flex-col">
            <BandPill band={band} />
          </div>
        </div>

        {/* Verdict, full-width along the bottom of the card */}
        {report.verdict_headline ? (
          <div className="mt-5 pt-5 border-t border-[#1a221c]">
            <p
              className="text-white text-sm sm:text-base leading-relaxed"
              data-testid="verdict-headline"
            >
              {report.verdict_headline}
            </p>
          </div>
        ) : null}
      </div>

      {/* Band-specific action button placed high. Existing CTABlock further down stays. */}
      <HeroBandCTA band={band} offerUrl={report?.offer_url} />

      {/* Headline stat cards */}
      <StatCards report={report} />

      {/* Dimension breakdown */}
      <div>
        <div className="flex items-baseline justify-between mb-3 sm:mb-4">
          <h2 className="text-[22px] sm:text-2xl font-bold text-white tracking-tight">
            The Scorecard
          </h2>
          <span className="gl-label-muted hidden sm:inline">Weighted out of 100</span>
        </div>
        <DimensionBars
          dimensions={report.dimensions}
          notes={report.dimension_notes}
          biggestGaps={report.biggest_gap_dimensions || []}
        />
      </div>

      {/* CTA + Reframe */}
      <CTABlock
        band={band}
        reframe={report?.gap_analysis?.suggested_reframe}
        projected={report?.gap_analysis?.projected_score}
        offerUrl={report?.offer_url}
      />

      {/* Earnings evidence */}
      <div>
        <div className="flex items-baseline justify-between mb-3 sm:mb-4">
          <h2 className="text-[22px] sm:text-2xl font-bold text-white flex items-baseline gap-2">
            Who Is Already Making Money
            {earningsCount > 0 ? (
              <span className="text-sm text-[#b6c1b8] font-medium tabular-nums">
                ({earningsCount} found)
              </span>
            ) : null}
          </h2>
        </div>
        <EarningsList earnings={report.earnings_evidence} />
      </div>

      {/* Sources, collapsed by default */}
      <CollapsibleSection
        title="The Sources We Used"
        count={sourcesCount}
        testId="sources-section"
        defaultOpen={false}
      >
        <SourcesList sources={report.numbered_sources && report.numbered_sources.length ? report.numbered_sources : report.all_sources} />
      </CollapsibleSection>

      {/* Share */}
      {shareUrl ? (
        <div className="gl-card-lift p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Share2 size={14} className="text-[#4ADE80]" />
              <span className="gl-label">Share this report</span>
            </div>
            <div className="text-sm text-[#b6c1b8] break-all">{shareUrl}</div>
          </div>
          <button
            type="button"
            onClick={onCopy}
            className="gl-ghost inline-flex items-center gap-2"
            data-testid="copy-share-link"
          >
            {copied ? <Check size={14} className="text-[#4ADE80]" /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StatCards({ report }) {
  const rev = report.revenue_potential || "";
  const split = rev ? splitRevenueRange(rev) : null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      <div className="gl-card-lift p-4 sm:p-5" data-testid="stat-revenue-potential">
        <div className="gl-label mb-2">Revenue Potential</div>
        {rev ? (
          split ? (
            <>
              <div className="text-[20px] sm:text-xl font-bold text-white leading-snug">
                Typical: {split.typical}
              </div>
              <div className="text-sm text-[#b6c8be] mt-2 font-semibold">
                Top operators: {split.top}
              </div>
              <div className="text-xs text-[#b6c8be] mt-2 leading-relaxed">
                Typical is the realistic band from operator self-reports. Top is a documented outlier, not a target.
              </div>
            </>
          ) : (
            <>
              <div className="text-[20px] sm:text-xl font-bold text-white leading-snug">
                {rev}
              </div>
              <div className="text-xs text-[#b6c8be] mt-2 leading-relaxed">
                Reported by real operators in the sources below. Not a promise.
              </div>
            </>
          )
        ) : (
          <>
            <div className="text-[20px] sm:text-xl font-bold text-white leading-snug">
              No same-model proof found
            </div>
            <div className="text-xs text-[#b6c8be] mt-2 leading-relaxed">
              We did not find verified earnings figures from operators running this exact business model. Treat any estimate as a guess.
            </div>
          </>
        )}
      </div>
      <div className="gl-card-lift p-4 sm:p-5" data-testid="stat-proof">
        <div className="gl-label mb-2">Standout Proof</div>
        {report.proof_headline ? (
          <>
            <div className="text-[18px] sm:text-lg font-bold text-white leading-snug">
              {report.proof_headline}
            </div>
            <div className="text-xs text-[#b6c8be] mt-2 leading-relaxed">
              Pulled from a real, linked, same-model operator. See the Reported Earnings cards below.
            </div>
          </>
        ) : (
          <>
            <div className="text-[18px] sm:text-lg font-bold text-white leading-snug">
              No verified figure found
            </div>
            <div className="text-xs text-[#b6c8be] mt-2 leading-relaxed">
              No same-model operator, FDD, or case study reported a number we could verify for this exact business.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
